/* eslint-disable no-param-reassign */
import { ImageAsset } from 'models/Asset'
import shaders from './shaders'
import { CHROMA_MAX_OPACITY } from '../../constant'

// https://jameshfisher.com/2020/08/11/production-ready-green-screen-in-the-browser/

// eslint-disable-next-line
const shader = shaders.find(shader => shader.name === 'chromakey')!.glsl

export function init(gl: WebGLRenderingContext) {
  const vs = gl.createShader(gl.VERTEX_SHADER)
  if (!vs) throw new Error('Could not create VERTEX_SHADER')
  gl.shaderSource(
    vs,
    'attribute vec2 c; void main(void) { gl_Position=vec4(c, 0.0, 1.0); }'
  )
  gl.compileShader(vs)

  const fs = gl.createShader(gl.FRAGMENT_SHADER)
  if (!fs) throw new Error('Could not create FRAGMENT_SHADER')
  gl.shaderSource(fs, shader)
  gl.compileShader(fs)
  if (!gl.getShaderParameter(fs, gl.COMPILE_STATUS)) {
    console.error(gl.getShaderInfoLog(fs))
  }

  const prog = gl.createProgram()
  if (!prog) throw new Error('Could not create WebGL Program')

  gl.attachShader(prog, vs)
  gl.attachShader(prog, fs)
  gl.linkProgram(prog)
  gl.useProgram(prog)

  const vb = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, vb)
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([ -1, 1, -1, -1, 1, -1, 1, 1 ]),
    gl.STATIC_DRAW
  )

  const coordLoc = gl.getAttribLocation(prog, 'c')
  gl.vertexAttribPointer(coordLoc, 2, gl.FLOAT, false, 0, 0)
  gl.enableVertexAttribArray(coordLoc)

  gl.activeTexture(gl.TEXTURE0)
  const tex = gl.createTexture()
  gl.bindTexture(gl.TEXTURE_2D, tex)

  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
  return prog
}

type WGL = {
  gl: WebGLRenderingContext;
  prog: WebGLProgram;
};

type LiveTweakableParams = {
  keycolor: readonly [number, number, number];
  similarity: number;
  smoothness: number;
  spill: number;
  opacity: number;
};

export function startProcessing(
  sourceImageEl: HTMLImageElement,
  displayCanvasEl: HTMLCanvasElement,
  wgl: WGL,
  getConfig: () => LiveTweakableParams
) {
  const { gl, prog } = wgl

  const texLoc = gl.getUniformLocation(prog, 'tex')
  const texWidthLoc = gl.getUniformLocation(prog, 'texWidth')
  const texHeightLoc = gl.getUniformLocation(prog, 'texHeight')
  const keyColorLoc = gl.getUniformLocation(prog, 'keyColor')
  const similarityLoc = gl.getUniformLocation(prog, 'similarity')
  const smoothnessLoc = gl.getUniformLocation(prog, 'smoothness')
  const spillLoc = gl.getUniformLocation(prog, 'spill')
  const opacityLoc = gl.getUniformLocation(prog, 'opacity')

  function processFrame() {
    if (sourceImageEl.width !== displayCanvasEl.width) {
      displayCanvasEl.width = sourceImageEl.width
      displayCanvasEl.height = sourceImageEl.height
      gl.viewport(0, 0, sourceImageEl.width, sourceImageEl.height)
    }

    gl.texImage2D(
      gl.TEXTURE_2D,
      0,
      gl.RGB,
      gl.RGB,
      gl.UNSIGNED_BYTE,
      sourceImageEl
    )
    gl.uniform1i(texLoc, 0)
    gl.uniform1f(texWidthLoc, sourceImageEl.width)
    gl.uniform1f(texHeightLoc, sourceImageEl.height)

    const config = getConfig()
    gl.uniform3f(
      keyColorLoc,
      config.keycolor[0],
      config.keycolor[1],
      config.keycolor[2]
    )
    gl.uniform1f(similarityLoc, config.similarity)
    gl.uniform1f(smoothnessLoc, config.smoothness)
    gl.uniform1f(spillLoc, config.spill)
    gl.uniform1f(opacityLoc, config.opacity)
    gl.drawArrays(gl.TRIANGLE_FAN, 0, 4)
  }

  processFrame()
}


export function getImageWithChromakeyUrl(asset: ImageAsset): Promise<string> {
  return new Promise((resolve: (image: HTMLImageElement) => void, reject) => {
    const image = new Image()
    image.width = asset.width
    image.height = asset.height
    image.crossOrigin = 'Anonymous'
    image.onload = () => resolve(image)
    image.onerror = reject
    image.src = asset.url ?? ''
  }).then(image => {
    const canvas = document.createElement('canvas')
    canvas.width = asset.width
    canvas.height = asset.height

    const gl = canvas.getContext('webgl', { premultipliedAlpha: false })

    if (!gl) return Promise.reject()

    const wgl = { gl, prog: init(gl) }

    const {
      chromaKeyColor,
      similarity,
      smoothness,
      spillReduction,
      chromaKeyOpacity,
    } = asset.settings

    const getConfig: () => LiveTweakableParams = () => {
      const { r, g, b } = chromaKeyColor
      return {
        keycolor: [ r / 255, g / 255, b / 255 ],
        similarity: similarity / 1000,
        smoothness: smoothness / 1000,
        spill: spillReduction / 1000,
        opacity: chromaKeyOpacity / CHROMA_MAX_OPACITY,
      }
    }

    startProcessing(image, canvas, wgl, getConfig)

    return new Promise((resolve: (url: string) => void) => window.requestAnimationFrame(() => {
      resolve(canvas.toDataURL())
    }))
  }).catch(() => asset.url ?? '')
}
